import { useSyncExternalStore } from 'react';
import type { DisconnectReason, Participant, RemoteTrack, Room, VideoTrack } from 'livekit-client';
import type { MeetingConnect } from '../api/client';
import { applyChosenDevices, onDeviceChosen, savedDevice, switchRoomDevice } from './audioDevices';
import { canShareScreen } from './patch';
import { shareProblemText } from './patchText';

/**
 * A meeting in the browser (1.13.0, Teampace 2707): one LiveKit room on the meeting's own page
 * (/m/<code>), for people in the company and guests alike. Like a Patch: a microphone, a camera and
 * a shared screen are sent; sound plays from hidden audio elements; every picture is marked
 * private, so live help and session recordings never include a meeting. Raise hand is a small
 * message to the room (LiveKit says who sent it).
 */

type LiveKit = typeof import('livekit-client');
let lk: LiveKit | null = null;
const sdk = async (): Promise<LiveKit> => (lk ??= await import('livekit-client'));

export interface MeetingPerson {
  id: string;
  name: string;
  me: boolean;
  guest: boolean;
  speaking: boolean;
  muted: boolean;
  camera: boolean;
  sharing: boolean;
  hand: boolean;
  /** May talk and share (a webinar's watchers may not). */
  publish: boolean;
}

export type MeetingPhase = 'idle' | 'joining' | 'connected' | 'reconnecting' | 'ended';

export interface MeetingState {
  phase: MeetingPhase;
  people: MeetingPerson[];
  publish: boolean;
  muted: boolean;
  micProblem: string | null;
  camera: boolean;
  cameraProblem: string | null;
  sharing: boolean;
  canShare: boolean;
  shareProblem: string | null;
  hand: boolean;
  screens: { id: string; name: string }[];
  watching: string | null;
  needsClick: boolean;
  /** Why it ended, when it wasn't their own choice. */
  notice: string | null;
  startedAt: number;
  /** Which room: 0 the meeting's own, else a breakout room (2708), and its name. */
  room: number;
  roomName: string;
  /** Stitch is moving this person (to a breakout room, or back with 0) when `at` comes. */
  moving: { to: number; name: string; at: number } | null;
  /** The meeting is being recorded (2710): everyone in it is told. */
  recording: boolean;
}

const IDLE: MeetingState = {
  phase: 'idle',
  people: [],
  publish: false,
  muted: true,
  micProblem: null,
  camera: false,
  cameraProblem: null,
  sharing: false,
  canShare: false,
  shareProblem: null,
  hand: false,
  screens: [],
  watching: null,
  needsClick: false,
  notice: null,
  startedAt: 0,
  room: 0,
  roomName: '',
  moving: null,
  recording: false
};

let state: MeetingState = IDLE;
const listeners = new Set<() => void>();
const set = (next: Partial<MeetingState>) => {
  state = { ...state, ...next };
  for (const l of [...listeners]) l();
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => void listeners.delete(l);
};

/** The meeting now, and every change (headset buttons and lights, 3141). */
export const meetingNow = (): MeetingState => state;
export const onMeetingState = (l: () => void): (() => void) => subscribe(l);

export function useMeeting(): MeetingState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

let room: Room | null = null;
let ticket = 0;
let audioBox: HTMLDivElement | null = null;
/** Raised hands, by identity (refreshed while raised, so someone who comes in later sees them). */
const hands = new Map<string, number>();
let handTimer: ReturnType<typeof setInterval> | null = null;
const HAND_TOPIC = 'hand';
const BREAKOUT_TOPIC = 'breakout';
const RECORDING_TOPIC = 'recording';
const HAND_FRESH_MS = 25_000;

function sounds(): HTMLDivElement {
  if (!audioBox || !audioBox.isConnected) {
    audioBox = document.createElement('div');
    audioBox.hidden = true;
    audioBox.setAttribute('aria-hidden', 'true');
    audioBox.setAttribute('data-private', '');
    document.body.appendChild(audioBox);
  }
  return audioBox;
}

const isGuest = (identity: string) => identity.startsWith('guest-');
/** A guest's name comes as "Jo Brown (guest)": shown as "Jo Brown", with the guest mark beside it. */
const nameOf = (p: Participant) => (isGuest(p.identity) ? (p.name ?? '').replace(/ \(guest\)$/, '') : p.name) || 'Someone';

function refresh(): void {
  const r = room;
  const L = lk;
  if (!r || !L) return;
  const { Track } = L;
  const now = Date.now();
  for (const [id, at] of hands) if (now - at > HAND_FRESH_MS) hands.delete(id);
  const all: Participant[] = [r.localParticipant, ...r.remoteParticipants.values()];
  const showing = (p: Participant) => {
    const pub = p.getTrackPublication(Track.Source.Camera);
    return Boolean(pub?.videoTrack && !pub.isMuted);
  };
  const people = all.map((p) => ({
    id: p.identity,
    name: nameOf(p),
    me: p === r.localParticipant,
    guest: isGuest(p.identity),
    speaking: p.isSpeaking,
    muted: !p.isMicrophoneEnabled,
    camera: showing(p),
    sharing: p.isScreenShareEnabled,
    hand: p === r.localParticipant ? state.hand : hands.has(p.identity),
    publish: p.permissions?.canPublish ?? true
  }));
  const screens = [...r.remoteParticipants.values()].filter((p) => Boolean(p.getTrackPublication(Track.Source.ScreenShare)?.videoTrack)).map((p) => ({ id: p.identity, name: nameOf(p) }));
  const watching = state.watching && screens.some((s) => s.id === state.watching) ? state.watching : (screens[0]?.id ?? null);
  const publish = r.localParticipant.permissions?.canPublish ?? state.publish;
  set({
    people,
    screens,
    watching,
    publish,
    muted: !r.localParticipant.isMicrophoneEnabled,
    camera: r.localParticipant.isCameraEnabled,
    sharing: r.localParticipant.isScreenShareEnabled
  });
}

export function cameraOf(identity: string): VideoTrack | null {
  if (!lk || !room) return null;
  const p: Participant | undefined = room.localParticipant.identity === identity ? room.localParticipant : room.remoteParticipants.get(identity);
  return (p?.getTrackPublication(lk.Track.Source.Camera)?.videoTrack as VideoTrack | undefined) ?? null;
}

export function screenOf(identity: string): VideoTrack | null {
  if (!lk || !room) return null;
  return (room.remoteParticipants.get(identity)?.getTrackPublication(lk.Track.Source.ScreenShare)?.videoTrack as VideoTrack | undefined) ?? null;
}

function deviceProblem(err: unknown, what: 'microphone' | 'camera'): string {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return `This page can’t use a ${what}: it needs a secure (https) connection.`;
  if (!lk) return `Your ${what} couldn’t start. Try again.`;
  const { MediaDeviceFailure } = lk;
  switch (MediaDeviceFailure.getFailure(err)) {
    case MediaDeviceFailure.PermissionDenied:
      return `Your browser is blocking the ${what} for this page. Allow it in the site settings (by the address bar), then try again.`;
    case MediaDeviceFailure.NotFound:
      return `No ${what} was found.`;
    case MediaDeviceFailure.DeviceInUse:
      return `Another app is using your ${what}. Close it, then try again.`;
    default:
      return `Your ${what} couldn’t start. Try again.`;
  }
}

function goodbye(reason: DisconnectReason | undefined): string {
  if (!lk) return 'You left the meeting.';
  const { DisconnectReason } = lk;
  switch (reason) {
    case DisconnectReason.CLIENT_INITIATED:
      return 'You left the meeting.';
    case DisconnectReason.DUPLICATE_IDENTITY:
      return 'You joined this meeting from another tab or device, so this one left it.';
    case DisconnectReason.PARTICIPANT_REMOVED:
      return 'The host took you out of the meeting.';
    case DisconnectReason.ROOM_DELETED:
    case DisconnectReason.ROOM_CLOSED:
      return 'The meeting ended.';
    default:
      return 'The connection to the meeting was lost. Join again to rejoin.';
  }
}

onDeviceChosen((kind, id) => {
  const r = room;
  if (r) void switchRoomDevice(r, kind, id).catch(() => undefined);
});

/**
 * Joins with what Stitch gave (a member's token, or a guest's once let in). `carry`: moving between
 * rooms of the same meeting (breakout rooms): the clock keeps going, and the microphone and camera
 * stay as they were.
 */
export async function connectMeeting(join: MeetingConnect, carry = false): Promise<void> {
  const was = { startedAt: state.startedAt, muted: state.muted, camera: state.camera, hand: state.hand };
  // Still in one (another tab of the app's own): out of it quietly first.
  const old = room;
  room = null;
  if (old) {
    stopHands();
    old.removeAllListeners();
    await old.disconnect().catch(() => undefined);
    audioBox?.replaceChildren();
  }
  const mine = ++ticket;
  set({ ...IDLE, phase: 'joining', publish: join.publish, canShare: join.publish && canShareScreen(), room: join.room, roomName: join.roomName, recording: Boolean(join.recording) });
  let L: LiveKit;
  try {
    L = await sdk();
  } catch {
    if (mine === ticket) set({ phase: 'ended', notice: 'The meeting couldn’t load. Check your connection, then try again.' });
    return;
  }
  if (mine !== ticket) return;
  const r = new L.Room({
    adaptiveStream: true,
    dynacast: true,
    disconnectOnPageLeave: true,
    audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    videoCaptureDefaults: { resolution: L.VideoPresets.h720.resolution },
    publishDefaults: {
      simulcast: true,
      videoCodec: 'h264',
      videoEncoding: { maxBitrate: 2_500_000, maxFramerate: 30 },
      videoSimulcastLayers: [L.VideoPresets.h360, L.VideoPresets.h180],
      screenShareEncoding: L.ScreenSharePresets.h1080fps15.encoding
    }
  });
  room = r;
  const { RoomEvent, Track } = L;
  const again = () => room === r && refresh();
  for (const e of [
    RoomEvent.ParticipantConnected,
    RoomEvent.ParticipantDisconnected,
    RoomEvent.TrackPublished,
    RoomEvent.TrackUnpublished,
    RoomEvent.TrackMuted,
    RoomEvent.TrackUnmuted,
    RoomEvent.LocalTrackPublished,
    RoomEvent.LocalTrackUnpublished,
    RoomEvent.ActiveSpeakersChanged,
    RoomEvent.ParticipantNameChanged
  ])
    r.on(e, again);
  // A webinar's host made them a presenter (or back to watching): what they may send follows.
  r.on(RoomEvent.ParticipantPermissionsChanged, (_prev, p: Participant) => {
    if (room !== r) return;
    if (p === r.localParticipant) {
      const may = r.localParticipant.permissions?.canPublish ?? false;
      set({ publish: may, canShare: may && canShareScreen() });
      if (may && state.hand) void raiseHand(false);
    }
    refresh();
  });
  r.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
    if (track.kind === Track.Kind.Audio) sounds().appendChild(track.attach());
    again();
  });
  r.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
    for (const el of track.detach()) el.remove();
    again();
  });
  r.on(RoomEvent.DataReceived, (payload: Uint8Array, p?: Participant, _kind?: unknown, topic?: string) => {
    if (room !== r) return;
    // Where to go, from Stitch itself (a message with no sender: nobody in the room can send one).
    if (topic === BREAKOUT_TOPIC && !p) {
      try {
        const m = JSON.parse(new TextDecoder().decode(payload)) as { to?: unknown; name?: unknown; in?: unknown };
        if (typeof m.to === 'number' && typeof m.name === 'string') set({ moving: { to: m.to, name: m.name, at: Date.now() + Math.min(Math.max(Number(m.in) || 0, 0), 30) * 1000 } });
      } catch {
        // Not one of Stitch's.
      }
      return;
    }
    // Recording started or stopped (Stitch's own word, never someone in the room's).
    if (topic === RECORDING_TOPIC && !p) {
      try {
        const m = JSON.parse(new TextDecoder().decode(payload)) as { on?: unknown };
        if (typeof m.on === 'boolean') set({ recording: m.on });
      } catch {
        // Not one of Stitch's.
      }
      return;
    }
    if (topic !== HAND_TOPIC || !p) return;
    const up = new TextDecoder().decode(payload) === '1';
    if (up) hands.set(p.identity, Date.now());
    else hands.delete(p.identity);
    refresh();
  });
  r.on(RoomEvent.AudioPlaybackStatusChanged, () => room === r && set({ needsClick: !r.canPlaybackAudio }));
  r.on(RoomEvent.Reconnecting, () => room === r && set({ phase: 'reconnecting' }));
  r.on(RoomEvent.Reconnected, () => room === r && (set({ phase: 'connected' }), refresh()));
  r.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
    if (room !== r) return;
    room = null;
    stopHands();
    audioBox?.replaceChildren();
    set({ ...IDLE, phase: 'ended', notice: goodbye(reason) });
  });
  try {
    await r.connect(join.url, join.token);
  } catch {
    if (room === r) {
      room = null;
      r.removeAllListeners();
      set({ phase: 'ended', notice: 'Couldn’t connect to the meeting. Check your connection, then try again.' });
    }
    return;
  }
  if (room !== r) {
    void r.disconnect();
    return;
  }
  set({ phase: 'connected', startedAt: carry && was.startedAt ? was.startedAt : Date.now(), needsClick: !r.canPlaybackAudio, hand: carry && was.hand });
  refresh();
  handTimer = setInterval(() => {
    if (state.hand) void sendHand(true);
    refresh();
  }, 10_000);
  if (join.publish && !(carry && was.muted)) await setMic(true);
  if (join.publish && carry && was.camera) await toggleMeetingCamera();
}

/** The move is done (or called off): no countdown. */
export const clearMove = () => set({ moving: null });

/** The host started or stopped recording here: shown at once (everyone else hears it from Stitch). */
export const setRecording = (recording: boolean) => set({ recording });

function stopHands(): void {
  if (handTimer) clearInterval(handTimer);
  handTimer = null;
  hands.clear();
}

export async function leaveMeeting(): Promise<void> {
  ticket++;
  const r = room;
  room = null;
  stopHands();
  audioBox?.replaceChildren();
  set({ ...IDLE, phase: 'ended', notice: 'You left the meeting.' });
  if (r) {
    r.removeAllListeners();
    await r.disconnect().catch(() => undefined);
  }
}

export const resetMeeting = () => set({ ...IDLE });

async function setMic(on: boolean): Promise<void> {
  const r = room;
  if (!r) return;
  const id = on ? savedDevice('audioinput') : '';
  try {
    await r.localParticipant.setMicrophoneEnabled(on, id ? { deviceId: id } : undefined);
    if (room === r) set({ micProblem: null });
    if (on) void applyChosenDevices(r).catch(() => null);
  } catch (err) {
    if (room === r) set({ micProblem: deviceProblem(err, 'microphone') });
  }
  refresh();
}

export const toggleMeetingMute = () => setMic(state.muted);

export async function toggleMeetingCamera(): Promise<void> {
  const r = room;
  if (!r) return;
  set({ cameraProblem: null });
  try {
    await r.localParticipant.setCameraEnabled(!r.localParticipant.isCameraEnabled);
  } catch (err) {
    if (room === r) set({ cameraProblem: deviceProblem(err, 'camera') });
  }
  refresh();
}

export async function toggleMeetingShare(): Promise<void> {
  const r = room;
  if (!r || !lk) return;
  const on = !r.localParticipant.isScreenShareEnabled;
  set({ shareProblem: null });
  try {
    await r.localParticipant.setScreenShareEnabled(
      on,
      on ? { audio: true, selfBrowserSurface: 'exclude', surfaceSwitching: 'include', systemAudio: 'include', contentHint: 'detail' } : undefined,
      on ? { simulcast: false, screenShareEncoding: { ...lk.ScreenSharePresets.h1080fps15.encoding, priority: 'high' }, degradationPreference: 'maintain-resolution' } : undefined
    );
  } catch (err) {
    if (room === r) set({ shareProblem: shareProblemText(err) });
  }
  refresh();
}

async function sendHand(up: boolean): Promise<void> {
  await room?.localParticipant.publishData(new TextEncoder().encode(up ? '1' : '0'), { reliable: true, topic: HAND_TOPIC }).catch(() => undefined);
}

export async function raiseHand(up = !state.hand): Promise<void> {
  set({ hand: up });
  await sendHand(up);
  refresh();
}

export const watchMeetingScreen = (identity: string) => set({ watching: identity });

export async function startMeetingSound(): Promise<void> {
  await room?.startAudio().catch(() => undefined);
  set({ needsClick: Boolean(room && !room.canPlaybackAudio) });
}
